import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import './ApproveTransaction.scss';
import { formatCurrency } from '../../../../../../../../utils/functions';
import { getPaymentReceiptByTransactionId, markPaymentReceiptAsCancelled } from '../../../../../../../../crud/payments_api/payment_receipts';
import { registerCashPayment } from '../../../../../../../../crud/payments_api/caja_movements';
import { toast } from 'react-toastify';
import ReceiptDetails from '../../../../../Receipts/components/Table/components/ReceiptDetails/ReceiptDetails';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import ChangeMethod from '../../../../../Receipts/components/Table/components/ReceiptDetails/components/TransactionView/components/ChangeMethod/ChangeMethod';

interface RenderModalProps {
  cajas: any[];
  setCajas: (cajas: any[]) => void;
  transaction: any;
  show: boolean;
  setShow: (show: boolean) => void;
  transactions: any[];
  setTransactions: (transactions: any[]) => void;
  refreshCajas?: () => void;
}

const ApproveTransaction: React.FC<RenderModalProps> = ({
  cajas,
  setCajas,
  transaction,
  show,
  setShow,
  transactions,
  setTransactions,
  refreshCajas
}) => {
  const [finalAmount, setFinalAmount] = useState<number>(transaction.transaction_amount);
  const [receivedAmount, setReceivedAmount] = useState<number | undefined>(undefined);
  const [shouldRound, setShouldRound] = useState<boolean>(false);
  const [roundingMethod, setRoundingMethod] = useState<'up' | 'down'>('up');
  const [selectedWorkstation, setSelectedWorkstation] = useState<any>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [currentReceipt, setCurrentReceipt] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadingAprobar, setLoadingAprobar] = useState(false);
  const [showChangeMethod, setShowChangeMethod] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [loadingReject, setLoadingReject] = useState(false);

  // cálculo base del vuelto
  const rawChange = (receivedAmount ?? 0) - finalAmount;

  // cálculo final según redondeo o no
  let change: number;
  if (!shouldRound) {
    // sin redondeo: devolvemos la resta directamente
    change = rawChange;
  } else {
    // con redondeo a múltiplos de 10
    if (roundingMethod === 'up') {
      change = Math.ceil(rawChange / 10) * 10;
    } else {
      change = Math.floor(rawChange / 10) * 10;
    }
  }

  // Validación: comprobar si la caja seleccionada tiene suficiente efectivo para dar el cambio
  const insufficientChange = Boolean(
    selectedWorkstation &&
    typeof selectedWorkstation.current_amount === 'number' &&
    change > selectedWorkstation.current_amount
  );

  useEffect(() => {
    const loadModalData = async () => {
      if (!show) return;
      
      setLoading(true);
      
      try {
        // Reset form states when modal opens
        setReceivedAmount(undefined);
        setShouldRound(false);
        setRoundingMethod('up');
        setSelectedWorkstation(null);
        
        setFinalAmount(transaction.transaction_amount);
        
        // Load receipt data
        const response = await getPaymentReceiptByTransactionId(transaction.transaction_id);
        setCurrentReceipt(response.data);
        
        // Refresh cajas data to get latest amounts (will trigger the second useEffect)
        if (refreshCajas) {
          await refreshCajas();
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    
    loadModalData();
  }, [show, transaction]);

  // Separate effect to update selected workstation when cajas change
  useEffect(() => {
    if (show && cajas && cajas.length > 0) {
      const openCajas = cajas.filter(c => c.is_open);
      if (openCajas.length > 0) {
        // If there's a selected workstation, update it with fresh data
        if (selectedWorkstation) {
          const updatedWorkstation = openCajas.find(c => c.workstation_id === selectedWorkstation.workstation_id);
          if (updatedWorkstation) {
            setSelectedWorkstation(updatedWorkstation);
          } else {
            // If the selected workstation is no longer available, select the first one
            setSelectedWorkstation(openCajas[0]);
          }
        } else {
          // No workstation selected yet, select the first open caja
          setSelectedWorkstation(openCajas[0]);
        }
      } else {
        // No open cajas available
        setSelectedWorkstation(null);
      }
    }
  }, [show, cajas]);

  const handleTransactionAsApproved = () => {
    if (!selectedWorkstation || !selectedWorkstation.workstation_id) {
      toast.error('Debe seleccionar una caja antes de aprobar.');
      return;
    }
    if (insufficientChange) {
      toast.error('No hay suficiente dinero en la caja para dar el cambio.');
      return;
    }
    setLoadingAprobar(true);
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const userId = Number(user.user_id) || 0;
    const amountToProcess = receivedAmount ?? 0;
    const rawChange = (receivedAmount ?? 0) - finalAmount;
    const roundedChange = shouldRound
      ? (roundingMethod === 'up'
          ? Math.ceil(rawChange / 10) * 10
          : Math.floor(rawChange / 10) * 10)
      : rawChange;
    registerCashPayment(
      selectedWorkstation.workstation_id,
      userId,
      amountToProcess,
      transaction.transaction_id,
      roundedChange,
      transaction.payment_type_id === 4 // is_transference
    )
      .then(async () => {
        // Remove transaction from pending list immediately
        setTransactions(transactions.filter(t => t.transaction_id !== transaction.transaction_id));
        
        // Refresh cajas data from server to get complete, updated data BEFORE closing modal
        if (refreshCajas) {
          await refreshCajas();
        }
        
        toast.success('Pago registrado');
        setShow(false);
      })
      .catch(err => {
        console.error(err);
        toast.error('Error registrando pago');
      })
      .finally(() => {
        setLoadingAprobar(false);
      });
  };

  const handleChangePaymentMethod = () => {
    setShowChangeMethod(true);
  };

  const onMethodChanged = async () => {
    // Remove the transaction from the pending list since it's been changed
    setTransactions(transactions.filter(t => t.transaction_id !== transaction.transaction_id));
    
    // Refresh cajas data to ensure consistency BEFORE closing modal
    if (refreshCajas) {
      await refreshCajas();
    }
    
    toast.success('Método de pago cambiado exitosamente');
    setShow(false);
  };

  const handleRejectPayment = async () => {
    if (!rejectComment.trim()) {
      toast.error('Debe ingresar un motivo para rechazar el pago');
      return;
    }

    if (!currentReceipt?.receipt_id) {
      toast.error('No se encontró el recibo asociado');
      return;
    }

    setLoadingReject(true);
    try {
      // Cancel the receipt with the rejection comment
      await markPaymentReceiptAsCancelled(currentReceipt.receipt_id, `Pago rechazado: ${rejectComment}`);
      
      // Remove the transaction from the pending list
      setTransactions(transactions.filter(t => t.transaction_id !== transaction.transaction_id));
      
      // Refresh cajas data
      if (refreshCajas) {
        await refreshCajas();
      }
      
      toast.success('Pago rechazado y recibo cancelado');
      setShowRejectModal(false);
      setShow(false);
    } catch (error) {
      console.error('Error rejecting payment:', error);
      toast.error('Error al rechazar el pago');
    } finally {
      setLoadingReject(false);
    }
  };

  return (
    <>
    <Modal show={show} onHide={() => setShow(false)} className="approve-transaction-modal" centered>
      <Modal.Header>
        <div className="form-modal-title-container">
          <Modal.Title className="form-modal-title">
            {transaction.payment_type_id === 4 ? 'Recibir Transferencia' : 'Recibir Efectivo'}
          </Modal.Title>
        </div>
        <Button variant="none" onClick={() => setShow(false)}>
          <X size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <MiniLoadingSpinner left="40%" />
        ) : (
          <div className="transaction-form">
            {/* Comment out Ver Detalle for now */}
            {/* <Button
              className="mp-button orange"
              variant="none"
              onClick={() => setShowDetails(true)}
            >
              <FaReceipt /> Ver Detalle <ChevronRight />
            </Button> */}

            {/* Total Amount */}
            <div className="form-section">
              <div className="section-header">
                <span className="section-label">Total a Pagar:</span>
                <span className="total-amount">{formatCurrency(finalAmount)}</span>
              </div>
            </div>

            {/* Received Amount Input */}
            <div className="form-section">
              <div className="field-label">Monto Recibido</div>
              <div className="amount-input-group with-equal-button">
                <div className="currency-symbol">$</div>
                <input
                  type="text"
                  className="amount-input"
                  value={
                    receivedAmount != null
                      ? receivedAmount.toLocaleString('es-CR', {
                          maximumFractionDigits: 0,
                        })
                      : ''
                  }
                  onChange={(e) => {
                    const onlyNums = e.target.value.replace(/\D/g, '');
                    setReceivedAmount(onlyNums ? Number(onlyNums) : undefined);
                  }}
                  placeholder="0"
                />
                {/* Quick-set button to accept exact amount (igualar al total) */}
                <button
                  type="button"
                  className="mp-button light equal-button received-equal-btn"
                  onClick={() => setReceivedAmount(finalAmount)}
                  disabled={receivedAmount === finalAmount}
                  title={receivedAmount === finalAmount ? 'Ya coincide' : 'Aceptar tal cual'}
                >
                  =
                </button>
              </div>
            </div>

            {/* Rounding Section */}
            <div className="form-section">
              <div className="rounding-section">
                <div className="rounding-header">
                  <label className="rounding-toggle">
                    <input
                      type="checkbox"
                      checked={shouldRound}
                      onChange={() => setShouldRound(!shouldRound)}
                    />
                    <div className="toggle-switch"></div>
                    <span className="toggle-label">Redondear vuelto</span>
                  </label>
                </div>
                
                {shouldRound && (
                  <div className="rounding-methods">
                    <div className="method-label">Método de redondeo:</div>
                    <div className="rounding-options">
                      <label className={`rounding-option ${roundingMethod === 'up' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="roundMethod"
                          value="up"
                          checked={roundingMethod === 'up'}
                          onChange={() => setRoundingMethod('up')}
                        />
                        <div className="option-content">
                          <div className="option-title">Al alza</div>
                          <div className="option-description">Redondea hacia arriba</div>
                        </div>
                      </label>
                      <label className={`rounding-option ${roundingMethod === 'down' ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="roundMethod"
                          value="down"
                          checked={roundingMethod === 'down'}
                          onChange={() => setRoundingMethod('down')}
                        />
                        <div className="option-content">
                          <div className="option-title">A la baja</div>
                          <div className="option-description">Redondea hacia abajo</div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Change Amount */}
            <div className="form-section">
              <div className="section-header">
                <span className="section-label">Cambio a Devolver:</span>
                <span className={`change-amount ${change < 0 ? 'negative' : change > 0 ? 'positive' : ''}`}>
                  {formatCurrency(Math.abs(change))}
                </span>
              </div>
            </div>

            {/* Caja Selection - Always show open cajas */}
            {cajas.filter(c => c.is_open).length > 0 && (
              <div className="form-section">
                <div className="field-label">
                  {cajas.filter(c => c.is_open).length === 1 ? 'Caja Seleccionada' : 'Seleccionar Caja'}
                </div>
                <div className="caja-options">
                  {cajas.filter(c => c.is_open).map(caja => (
                    <React.Fragment key={caja.workstation_id}>
                      <div
                        className={`caja-option ${selectedWorkstation?.workstation_id === caja.workstation_id ? 'selected' : ''} ${cajas.filter(c => c.is_open).length === 1 ? 'single-option' : ''}`}
                        onClick={() => setSelectedWorkstation(caja)}
                      >
                        <div className="caja-name">{caja.workstation_name}</div>
                        <div className="caja-amount">{formatCurrency(caja.current_amount)}</div>
                      </div>
                      {/* Show error directly under the selected caja when not enough funds */}
                      {selectedWorkstation?.workstation_id === caja.workstation_id && insufficientChange && (
                        <div className="caja-option-error" key={`err-${caja.workstation_id}`}>
                          No hay suficiente dinero en esta caja para dar cambio ({formatCurrency(caja.current_amount)} disponible).
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {currentReceipt && showDetails && (
          <ReceiptDetails
            setReceipts={() => {}}
            currentReceipt={currentReceipt}
            show={showDetails}
            onClose={() => setShowDetails(false)}
          />
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="form-modal-footer-custom">
          <Button variant="none" className="form-button-cancel" onClick={() => setShow(false)}>
            Cancelar
          </Button>
          <Button 
            variant="none" 
            className="form-button-change-method" 
            onClick={handleChangePaymentMethod}
            disabled={loadingAprobar}
          >
            Cambiar Método
          </Button>
          <Button 
            variant="none" 
            className="form-button-reject" 
            onClick={() => setShowRejectModal(true)}
            disabled={loadingAprobar}
            style={{ backgroundColor: '#dc2626', color: 'white' }}
          >
            Rechazar
          </Button>
          <Button
            variant="none"
            className="form-button-create"
            onClick={handleTransactionAsApproved}
            disabled={loadingAprobar || !selectedWorkstation || receivedAmount == null || receivedAmount < finalAmount || insufficientChange}
          >
            {loadingAprobar ? (
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              'Aprobar'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
    
    {/* Change Method Modal */}
    {showChangeMethod && (
      <ChangeMethod
        transaction={transaction}
        show={showChangeMethod}
        onHide={() => setShowChangeMethod(false)}
        onMethodChanged={onMethodChanged}
      />
    )}

    {/* Reject Payment Modal */}
    <Modal 
      show={showRejectModal} 
      onHide={() => {
        setShowRejectModal(false);
        setRejectComment('');
      }}
      centered
      className="reject-payment-modal"
    >
      <Modal.Header>
        <Modal.Title style={{ color: '#dc2626' }}>
          Rechazar Pago en Efectivo
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ marginBottom: '1rem' }}>
          <p style={{ marginBottom: '0.5rem', fontWeight: '500' }}>
            Esta acción rechazará el pago en efectivo y cancelará el recibo asociado.
          </p>
          <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
            Recibo #{currentReceipt?.receipt_id} - {formatCurrency(transaction.transaction_amount)}
          </p>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
            Motivo del rechazo <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <textarea
            className="form-control"
            rows={4}
            placeholder="Ingrese el motivo del rechazo (obligatorio)"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            disabled={loadingReject}
            style={{
              resize: 'vertical',
              minHeight: '100px'
            }}
          />
          <small style={{ color: '#6b7280', display: 'block', marginTop: '0.5rem' }}>
            Este comentario quedará registrado en el historial del recibo.
          </small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={() => {
            setShowRejectModal(false);
            setRejectComment('');
          }}
          disabled={loadingReject}
        >
          Cancelar
        </Button>
        <Button 
          variant="danger" 
          onClick={handleRejectPayment}
          disabled={!rejectComment.trim() || loadingReject}
        >
          {loadingReject ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                style={{ marginRight: '0.5rem' }}
              />
              Rechazando...
            </>
          ) : (
            'Confirmar Rechazo'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  </>
  );
};

export default ApproveTransaction;
